/**
 * Emote clips (+field): short celebratory poses played as a one-shot "ability" clip on heroes.
 * `emote.levelup`: a crouch to gather, then both arms flung up in a V, chest open, head back, a
 * little hop on the burst (timed to fx/celebrate.ts burstAt ≈ 0.34 s), a held beat and a recovery.
 * Full-body only while standing; running heroes keep their legs (mask 'auto').
 */
import { defineClip } from './clips';
import { registerAbilityClips } from './registry';

export const EMOTE_LEVELUP = 'emote.levelup';
export const EMOTE_CHEER = 'emote.cheer';

const levelUp = defineClip({
  id: EMOTE_LEVELUP,
  duration: 1.5,
  blendIn: 0.08,
  blendOut: 0.3,
  keys: [
    // Gather: knees bend, fists drawn in at the chest, head down.
    {
      t: 0.26,
      ease: 'out',
      pose: {
        hips: [0.1, 0, 0],
        spine: [0.25, 0, 0],
        head: [0.25, 0, 0],
        leftUpperArm: [0.6, 0.5, 0.2],
        rightUpperArm: [0.6, 0.5, 0.2],
        leftLowerArm: [1.6],
        rightLowerArm: [1.6],
        leftUpperLeg: [0.45],
        rightUpperLeg: [0.45],
        leftLowerLeg: [0.8],
        rightLowerLeg: [0.8],
      },
      root: { hop: -0.12 },
    },
    // Burst: arms thrown up and out, chest open to the sky, a hop.
    {
      t: 0.38,
      ease: 'back',
      overshoot: 1.6,
      hold: 0.55,
      pose: {
        spine: [-0.22, 0, 0],
        chest: [-0.18, 0, 0],
        neck: [-0.12, 0, 0],
        head: [-0.32, 0, 0],
        leftUpperArm: [2.5, -0.15, 0.75],
        rightUpperArm: [2.5, -0.15, 0.75],
        leftLowerArm: [0.15],
        rightLowerArm: [0.15],
        leftUpperLeg: [0.05, 0, 0.12],
        rightUpperLeg: [0.05, 0, 0.12],
      },
      root: { hop: 0.22 },
    },
    { t: 1.5, ease: 'inOut' },
  ],
  cues: [
    { t: 0.38, fx: 'holy', at: 'self', color: [1.0, 0.85, 0.45], scale: 1.2 },
    { t: 0.4, fx: 'dust', at: 'ground', scale: 0.8 },
  ],
});

/** Smaller cheer (bounty turn-in, first clear): one fist pumped up. */
const cheer = defineClip({
  id: EMOTE_CHEER,
  duration: 1.0,
  blendIn: 0.06,
  blendOut: 0.25,
  keys: [
    { t: 0.14, ease: 'out', pose: { rightUpperArm: [0.9, 0.3, 0.3], rightLowerArm: [1.9], spine: [0.08, 0, 0] } },
    { t: 0.26, ease: 'back', overshoot: 1.4, hold: 0.35, pose: { rightUpperArm: [2.6, 0, 0.3], rightLowerArm: [0.5], spine: [-0.1, 0, 0], head: [-0.15, 0, 0] }, root: { hop: 0.08 } },
    { t: 1.0, ease: 'inOut' },
  ],
});

let done = false;
/** Register the emote clips once (idempotent). */
export function registerEmotes(): void {
  if (done) return;
  done = true;
  registerAbilityClips(EMOTE_LEVELUP, { strike: levelUp });
  registerAbilityClips(EMOTE_CHEER, { strike: cheer });
}
